// src/admin/pages/ShareholdersManagementPage.js
import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  X, 
  User, 
  Building, 
  DollarSign,
  Calendar,
  Mail,
  Phone,
  MapPin
} from 'lucide-react';
import { supabase } from '../../config';
import EquityService from '../../services/equityService';
import ApiErrorBoundary from '../components/ApiErrorBoundary';

const ShareholdersManagementPage = () => {
  const [shareholders, setShareholders] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  // const [editingShareholder, setEditingShareholder] = useState(null); // TODO: Implement edit functionality
  const [apiError, setApiError] = useState(null);
  const [newShareholder, setNewShareholder] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    postcode: '',
    country: 'Australia',
    entity_type: 'Individual',
    company_id: '',
    shareholder_type: 'Investor'
  });

  const shareholderTypes = ['Founder', 'Management', 'Investor', 'Advisor', 'Employee', 'Other'];
  const entityTypes = ['Individual', 'Company', 'Trust', 'Organisation'];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Fetch shareholders with company info
      const { data: shareholdersData, error: shareholdersError } = await supabase
        .from('equity_shareholders')
        .select(`
          *,
          company:equity_companies!equity_shareholders_company_id_fkey(
            id,
            name,
            user_id
          )
        `)
        .order('created_at', { ascending: false });

      if (shareholdersError) throw shareholdersError;

      // Fetch companies
      const { data: companiesData, error: companiesError } = await supabase
        .from('equity_companies')
        .select('*')
        .order('created_at', { ascending: false });

      if (companiesError) throw companiesError;

      // Get unique user IDs from companies
      const userIds = [...new Set(companiesData.map(c => c.user_id).filter(Boolean))];
      
      // Fetch creator profiles
      let creators = {};
      if (userIds.length > 0) {
        const { data: creatorProfiles } = await supabase
          .from('user_profiles')
          .select('id, first_name, last_name, email')
          .in('id', userIds);
        
        if (creatorProfiles) {
          creators = creatorProfiles.reduce((acc, profile) => {
            acc[profile.id] = profile;
            return acc;
          }, {});
        }
      }

      // Enrich shareholders with creator info
      const enrichedShareholders = (shareholdersData || []).map(shareholder => ({
        ...shareholder,
        company: shareholder.company ? {
          ...shareholder.company,
          creator: creators[shareholder.company.user_id] || null
        } : null
      }));

      setShareholders(enrichedShareholders);
      setCompanies(Array.isArray(companiesData) ? companiesData : []);
      setApiError(null);
    } catch (error) {
      console.error('Error loading data:', error);
      setShareholders([]);
      setCompanies([]);
      setApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateShareholder = async () => {
    try {
      if (!newShareholder.company_id) {
        alert('Please select a company');
        return;
      }
      
      await EquityService.createShareholder(newShareholder);
      await loadData();
      setNewShareholder({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        postcode: '',
        country: 'Australia',
        entity_type: 'Individual',
        company_id: '',
        shareholder_type: 'Investor'
      });
      setShowCreateModal(false);
    } catch (error) {
      console.error('Error creating shareholder:', error);
    }
  };

  // TODO: Implement edit functionality
  // const handleUpdateShareholder = async (id, updatedData) => {
  //   try {
  //     await EquityService.updateShareholder(id, updatedData);
  //     await loadData();
  //     setEditingShareholder(null);
  //   } catch (error) {
  //     console.error('Error updating shareholder:', error);
  //   }
  // };

  const handleDeleteShareholder = async (id) => {
    if (!window.confirm('Are you sure you want to delete this shareholder? This action cannot be undone.')) {
      return;
    }

    try {
      await EquityService.deleteShareholder(id);
      await loadData();
    } catch (error) {
      console.error('Error deleting shareholder:', error);
    }
  };

  const getCompanyName = (shareholder) => {
    if (shareholder.company && typeof shareholder.company === 'object') {
      return shareholder.company.name || 'Unknown Company';
    }
    const companyId = typeof shareholder === 'object' ? shareholder.company_id : shareholder;
    const company = companies.find(c => c.id === companyId);
    return company ? company.name : 'Unknown Company';
  };

  const getShareholderTypeColor = (type) => {
    const colors = {
      'Founder': 'bg-purple-100 text-purple-800',
      'Management': 'bg-blue-100 text-blue-800',
      'Investor': 'bg-green-100 text-green-800',
      'Advisor': 'bg-yellow-100 text-yellow-800',
      'Employee': 'bg-gray-100 text-gray-800',
      'Other': 'bg-red-100 text-red-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading shareholders...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <ApiErrorBoundary error={apiError} onRetry={loadData}>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Shareholders Management</h2>
          <p className="text-gray-600 mt-1">Manage shareholders across all companies</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Shareholder
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <User className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Shareholders</p>
              <p className="text-2xl font-bold text-gray-900">{shareholders.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <Building className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Companies</p>
              <p className="text-2xl font-bold text-gray-900">{companies.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <DollarSign className="h-8 w-8 text-purple-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Investors</p>
              <p className="text-2xl font-bold text-gray-900">{shareholders.filter(s => s.shareholder_type === 'Investor').length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <Calendar className="h-8 w-8 text-orange-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">This Month</p>
              <p className="text-2xl font-bold text-gray-900">
                {shareholders.filter(s => {
                  const created = new Date(s.created_at);
                  const now = new Date();
                  return created.getMonth() === now.getMonth() && created.getFullYear() === now.getFullYear();
                }).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Shareholders Table */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Shareholder
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Company
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Location
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Creator
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {shareholders.map((shareholder) => (
                <tr key={shareholder.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                          <User className="h-5 w-5 text-green-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {shareholder.first_name} {shareholder.last_name}
                        </div>
                        <div className="text-sm text-gray-500">{shareholder.entity_type}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getShareholderTypeColor(shareholder.shareholder_type)}`}>
                      {shareholder.shareholder_type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      <Building className="h-4 w-4 text-gray-400 mr-1" />
                      {getCompanyName(shareholder)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      <Mail className="h-4 w-4 text-gray-400 mr-1" />
                      {shareholder.email || 'N/A'}
                    </div>
                    <div className="flex items-center text-gray-500">
                      <Phone className="h-4 w-4 text-gray-400 mr-1" />
                      {shareholder.phone || 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      <MapPin className="h-4 w-4 text-gray-400 mr-1" />
                      {shareholder.city}, {shareholder.state}
                    </div>
                    <div className="text-gray-500">{shareholder.country}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {shareholder.company?.creator 
                      ? ((shareholder.company.creator.first_name && shareholder.company.creator.last_name)
                          ? `${shareholder.company.creator.first_name} ${shareholder.company.creator.last_name}`
                          : shareholder.company.creator.first_name || shareholder.company.creator.last_name || shareholder.company.creator.email || 'Unknown')
                      : 'Unknown'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        // onClick={() => setEditingShareholder(shareholder)} // TODO: Implement edit functionality
                        className="text-blue-600 hover:text-blue-900 opacity-50 cursor-not-allowed"
                        disabled
                        title="Edit functionality coming soon"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteShareholder(shareholder.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {shareholders.length === 0 && (
          <div className="text-center py-12">
            <User className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No shareholders</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by adding a new shareholder.</p>
            <div className="mt-6">
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Shareholder
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Shareholder Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Add New Shareholder</h3>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <form onSubmit={(e) => { e.preventDefault(); handleCreateShareholder(); }}>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">First Name *</label>
                      <input
                        type="text"
                        required
                        value={newShareholder.first_name}
                        onChange={(e) => setNewShareholder({...newShareholder, first_name: e.target.value})}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Last Name *</label>
                      <input
                        type="text"
                        required
                        value={newShareholder.last_name}
                        onChange={(e) => setNewShareholder({...newShareholder, last_name: e.target.value})}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email *</label>
                    <input
                      type="email"
                      required
                      value={newShareholder.email}
                      onChange={(e) => setNewShareholder({...newShareholder, email: e.target.value})}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Phone</label>
                    <input
                      type="tel"
                      value={newShareholder.phone}
                      onChange={(e) => setNewShareholder({...newShareholder, phone: e.target.value})}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Company *</label>
                    <select
                      required
                      value={newShareholder.company_id}
                      onChange={(e) => setNewShareholder({...newShareholder, company_id: e.target.value})}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select Company</option>
                      {companies.map(company => (
                        <option key={company.id} value={company.id}>
                          {company.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Shareholder Type</label>
                      <select
                        value={newShareholder.shareholder_type}
                        onChange={(e) => setNewShareholder({...newShareholder, shareholder_type: e.target.value})}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      >
                        {shareholderTypes.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Entity Type</label>
                      <select
                        value={newShareholder.entity_type}
                        onChange={(e) => setNewShareholder({...newShareholder, entity_type: e.target.value})}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      >
                        {entityTypes.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Address</label>
                    <input
                      type="text"
                      value={newShareholder.address}
                      onChange={(e) => setNewShareholder({...newShareholder, address: e.target.value})}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">City</label>
                      <input
                        type="text"
                        value={newShareholder.city}
                        onChange={(e) => setNewShareholder({...newShareholder, city: e.target.value})}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">State</label>
                      <input
                        type="text"
                        value={newShareholder.state}
                        onChange={(e) => setNewShareholder({...newShareholder, state: e.target.value})}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Postcode</label>
                      <input
                        type="text"
                        value={newShareholder.postcode}
                        onChange={(e) => setNewShareholder({...newShareholder, postcode: e.target.value})}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Country</label>
                    <input
                      type="text"
                      value={newShareholder.country}
                      onChange={(e) => setNewShareholder({...newShareholder, country: e.target.value})}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-blue-700"
                  >
                    Add Shareholder
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      </ApiErrorBoundary>
    </div>
  );
};

export default ShareholdersManagementPage;
